import { BsChevronRight } from '@react-icons/all-files/bs/BsChevronRight';

import { CloudinaryImage } from 'components/common/CloudinaryImage';
import { Link } from 'components/common/Link';
import { Slideout, SlideoutCommonProps } from 'components/design-system/Slideout';

const steps = [
  {
    label: 'STEP 1',
    title: 'Print your shipping label',
    description:
      'Use the prepaid shipping label provided below. You will receive one prepaid shipping label for each item you sell on the platform.',
    image: null
  },
  {
    label: 'STEP 2',
    title: 'Get two new, sturdy boxes that fit inside each other',
    description: 'Double boxing is required for insurance purposes, please ensure the boxes fit snugly.',
    image: '/shipping_instructions_step_2.png'
  },
  {
    label: 'STEP 3',
    title: 'Print your shipping label',
    description:
      'Use the prepaid shipping label provided below. You will receive one prepaid shipping label for each item you sell on the app.',
    image: '/shipping_instructions_step_3.png'
  },
  {
    label: 'TIP',
    title: 'Shipping just the watch?',
    description: [
      'Wrap the watch in a soft cloth or sock, especially if it has a bracelet',
      'Then place in a hard outer case such as: sunglass case, old iPhone box, or plastic case.',
      'We also recommend placing this in a Ziploc® bag to prevent moisture or water damage during transit.'
    ],
    image: '/shipping_instructions_tip.png'
  },
  {
    label: 'STEP 4',
    title: 'Tape up the inner box and place it inside the outer box',
    description:
      'Please make sure the boxes are as tight of a fit as possible. The inner box should not move around inside the outer box.',
    image: '/shipping_instructions_step_4.png'
  },
  {
    label: 'STEP 5',
    title: 'Properly tape and secure the outer box',
    description:
      'Please use the H-method for taping the box. Seal the box across the side flaps and the seam on both the top and bottom of the box using 6 strips of tape. DO NOT use masking tape, cellophane tape, duct tape, string, or paper over-wrap to secure the box.',
    image: '/shipping_instructions_step_5.png'
  },
  {
    label: 'STEP 6',
    title: 'Tape the pre-paid shipping label to the outer box',
    description: 'Please use the same quality of tape you used to seal the box.',
    image: '/shipping_instructions_step_6.png'
  },
  {
    label: 'STEP 7',
    title: 'Take the box to your nearest FedEx facility within 2 business days',
    description:
      'Bezel will not be liable for packages that are tendered to the applicable courier via on-site pickup or third-party access points (e.g., Staples, CVS Pharmacy, UPS Access Point Drop-off Lockers, etc.)',
    image: '/shipping_instructions_step_7.png'
  }
];

const labelButtonStyle = {
  border: '1px solid rgb(7, 116, 91, 0.6)',
  borderColor: 'rgb(7, 116, 91)',
  backgroundColor: 'rgba(7, 116, 91, 0.02)',
  color: 'rgb(7, 116, 91)'
};

type ShippingInstructionsModalProps = {
  labelUrl: string;
} & SlideoutCommonProps;

const ShippingInstructionsStep: React.FC<{ step: (typeof steps)[0]; labelUrl?: string }> = ({ step, labelUrl }) => (
  <div className='p-3 rounded-2 border riforma-regular'>
    {step.image && (
      <div className='mb-3 rounded-2 overflow-hidden w-100'>
        <CloudinaryImage responsive src={step.image} alt={step.title} sizes='50vw' />
      </div>
    )}
    <div className='text-secondary fs-12px mb-2'>{step.label}</div>
    <div className='text-primary fs-16px mb-3'>{step.title}</div>
    <div className='text-secondary fs-14px'>
      {Array.isArray(step.description) ? (
        <ul className='mb-0 gap-2 ps-3'>
          {step.description.map((item, index) => (
            <li key={index} className='my-1'>
              {item}
            </li>
          ))}
        </ul>
      ) : (
        step.description
      )}
    </div>
    {labelUrl && (
      <Link href={labelUrl} target='_blank'>
        <div className='d-flex align-items-center justify-content-between p-4 rounded-2 mt-3' style={labelButtonStyle}>
          <span className='riforma-medium fs-14px'>Print label</span>
          <div className='d-flex align-items-center gap-2'>
            <CloudinaryImage alt='Fedex logo' src='fedex-logo.png' width={60} height={20} />
            <BsChevronRight />
          </div>
        </div>
      </Link>
    )}
  </div>
);

export const ShippingInstructionsModal: React.FC<ShippingInstructionsModalProps> = ({ labelUrl, ...props }) => (
  <Slideout title='Shipping Instructions' {...props}>
    <div className='text-center'>
      <p className='riforma-regular text-secondary px-3 py-4'>
        Please follow these steps to ensure your watch sale goes smoothly
      </p>
    </div>
    {steps.map((step, index) => (
      <div key={index} className='mb-4'>
        <ShippingInstructionsStep step={step} labelUrl={index === 0 ? labelUrl : undefined} />
      </div>
    ))}
    <div className='mb-6' />
  </Slideout>
);
